"use client";

import React, { useMemo, useCallback, useState } from "react";
import { cn, dayjs } from "utils";
import { LottieLoading, VirtualList } from "@ospweb/sdk-ui";
import { Calendar } from "../../Calendar";
import { EmptyState } from "../../Empty";
import { EarningsCalendarProps } from "./types";
import { EarningsCalendarHeader } from "./EarningsCalendarHeader";
import { EarningsCalendarItem } from "./EarningsCalendarItem";
import { SectionHeader } from "@/src/components/SectionHeader";
import Translations from "@translate/components/Home/EarningsCalendar/EarningsCalendar";
import { I18nUtil } from "@utils/i18n";
import {
  ContentCategoryEnum,
  ContentTypeEnum,
} from "@/src/features/api/openData";
import { useContents } from "@/src/hooks";
import { useStartObserveHeight } from "@/src/mobiles/mainContainer/HeightRecorderContext";
const I18n = I18nUtil.createScoped(Translations);

/**
 * @description Earnings calendar component for displaying company earnings information
 */
const EarningsCalendarComponent: React.FC<EarningsCalendarProps> = ({
  className,
  requestParams,
}) => {
  const now = new Date();
  const minDate = new Date(now.getFullYear(), 0, 1);
  const maxDate = new Date(now);
  maxDate.setMonth(maxDate.getMonth() + 1);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const { data, isPending, isFetching, hasNextPage, fetchNextPage } =
    useContents({
      initialPageParam: {
        limit: 20,
        offset: 0,
        type: ContentTypeEnum.EARNING_CALENDAR,
        category: ContentCategoryEnum.STOCK,
        ...requestParams,
        start_time: dayjs(selectedDate).startOf("day").valueOf(),
        end_time: dayjs(selectedDate).endOf("day").valueOf(),
      },
    });
  useStartObserveHeight(data.length === 0 && isPending, !!data?.length);
  const handleEndReached = useCallback(() => {
    if (hasNextPage && !isFetching) {
      fetchNextPage?.();
    }
  }, [hasNextPage, isFetching, fetchNextPage]);

  const loadingComponent = useMemo(
    () => (
      <div className={cn("flex flex-col items-center justify-center py-2")}>
        <LottieLoading style={{ width: 20, height: 20 }} />
      </div>
    ),
    [],
  );
  return (
    <div className="flex flex-col gap-2 w-full">
      <SectionHeader
        title={I18n.t("Earnings Calendar")}
        showChevron={false}
        className="md:hidden flex"
        rightSlot={
          <div className="text-content_primary font-medium text-xs leading-4 whitespace-nowrap">
            {dayjs(selectedDate).format("MMM DD, YYYY")}
          </div>
        }
      />

      <div
        className={cn(
          "flex flex-col items-start p-4 bg-background_white_overlay5 rounded-xl max-h-[calc(100vh-96px)] min-h-[460px]",
          className,
        )}
      >
        {/* Header component */}
        <EarningsCalendarHeader
          date={dayjs(selectedDate).format("MMM DD, YYYY")}
        />
        <Calendar
          className="mt-2 mb-2"
          value={selectedDate}
          onChange={setSelectedDate}
          disabled={[{ before: minDate }, { after: maxDate }]}
        />
        {/* Items list */}
        {!isPending && data?.length === 0 && (
          <EmptyState
            title={I18n.t("No Earning Reports Today")}
            description={I18n.t("Select another date to view reports.")}
            descriptionClassName="max-w-[168px]"
            className="h-full mx-auto my-auto"
          />
        )}
        {data.length === 0 && isPending && (
          <div
            className={cn(
              "flex flex-col items-center justify-center py-2 w-full h-full my-auto",
            )}
          >
            <LottieLoading style={{ width: 20, height: 20 }} />
          </div>
        )}
        {data.length > 0 && (
          <div className="max-h-[calc(100%-24px)] overflow-y-auto no-scrollbar w-full">
            <VirtualList
              hasNextPage={hasNextPage}
              onEndReached={handleEndReached}
              onEndReachedThreshold="100"
              renderLoading={!!fetchNextPage && loadingComponent}
            >
              {data.map((item, index) => (
                <EarningsCalendarItem
                  key={`${item.id}-${index}`}
                  item={item}
                  isLast={index === data.length - 1}
                  index={index}
                />
              ))}
            </VirtualList>
          </div>
        )}
      </div>
    </div>
  );
};

export const EarningsCalendar = React.memo(
  EarningsCalendarComponent,
  (prev, next) => prev.className === next.className,
);
